import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';

import ColorBlockComponent from 'webapp/components/color-block';
import CommonPopupsColorPickerComponent from 'webapp/components/common/popups/color-picker';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

export interface Option {
	id: string | number;
	hex: string;
	name?: string;
	selected?: boolean;
	disabled?: boolean;
}

export default class CommonPopupColorsContextComponent extends PopupInnerContextComponent<PopupData.Common.Colors> {
	@service('constants') declare constantsService: Service.Constants;
	@service('popup') declare popupService: Service.Popup;

	get selected() {
		return this.popupData.selected ?? '';
	}

	get selectedOption() {
		return this.shownColors.find((item) => item.selected);
	}

	get selectedColorName() {
		return this.selectedOption?.name || '';
	}

	get allowEmpty() {
		return this.popupData.allowEmpty;
	}

	get emptyText() {
		return this.popupData.emptyText || 'Transparent';
	}

	get allowCustom() {
		return !!this.popupData.allowCustom;
	}

	get isCustomSelected() {
		return this.selected && !this.selectedOption;
	}

	get selectedCustom() {
		return this.isCustomSelected ? (this.selected as string) : '';
	}

	get allColors(): Option[] {
		return (
			this.popupData.allColors ||
			this.constantsService.boardColors
				.filter((item) => item.name)
				.map((color) => {
					return {
						id: color.hex,
						hex: color.hex,
					};
				})
		);
	}

	get shownColors(): Option[] {
		const shownColors = this.allColors;
		shownColors.forEach((colorData) => {
			colorData.selected = colorData.id === this.selected;
		});
		return shownColors;
	}

	@action
	changeColor(option: Option | null): void {
		if (option?.disabled) {
			return;
		}
		this.popupData.changeColor(option ? option.id : '');
		this.close();
	}

	@action
	changeCustomColor(color: string) {
		if (color) {
			let hex = color;
			if (!hex.startsWith('#')) {
				hex = `#${hex}`;
			}
			this.changeColor({ id: hex, hex });
		}
	}

	@action
	openColorPicker(e: PointerEvent) {
		const attachTo = e.target as HTMLElement;
		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createContext(CommonPopupsColorPickerComponent, attachTo, {
			popupClass: 'popup-type-color-picker',
			theme: this.args.model.theme,
		});

		return popup.open({
			value: this.selectedCustom,
			onChange: this.changeCustomColor,
			hasSubmit: true,
		});
	}
	<template>
		<a role="button" class="close" {{on "click" this.close}}>
			<CommonUiIconComponent @name="Close" @size="sm" />
		</a>
		<div class="context-ct">
			{{#if this.selectedColorName}}
				<span class="name">{{this.selectedColorName}}</span>
			{{/if}}
			{{#if this.allowEmpty}}
				<a class="transparent" {{on "click" (fn this.changeColor null)}}>
					<CommonUiIconComponent @name="RaindropAuto" @size="sm" />
					{{this.emptyText}}
				</a>
			{{/if}}
			<ul>
				{{#each this.shownColors as |color|}}
					<li class="{{if color.selected 'selected'}} {{if color.disabled 'disabled'}}">
						<a role="button" {{on "click" (fn this.changeColor color)}}>
							<ColorBlockComponent @color={{color.hex}} />
						</a>
					</li>
				{{/each}}
				{{#if this.allowCustom}}
					<li class="custom-color {{if this.isCustomSelected 'selected'}}">
						<a role="button" {{on "click" this.openColorPicker}}>
							<ColorBlockComponent @color="" />
						</a>
					</li>
				{{/if}}
			</ul>
		</div>
	</template>
}
